import { memo, useCallback } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Clock, Hourglass, MessageCircle, XCircle } from "lucide-react";
import { formatDuration } from "@/components/agent/display/utils/formatters";
import { GenieAvatar } from "@/components/GenieAvatar";
import { cn } from "@/lib/utils";
import { Conversation } from "@/types/conversation/conversation";
import { TranscriptCopyButton } from "./details/TranscriptCopyButton";
import {
  ConversationAttentionPill,
  ConversationAnnotationPills,
  ConversationChannelPill,
  extractConversationSignals,
} from "./conversationListShared";

interface ConversationRowsViewProps {
  conversations: Conversation[];
  onViewDetails: (conversation: Conversation) => void;
}

/**
 * Hybrid view — one wide card per conversation. Keeps the avatar + branding
 * presence of the card grid but exposes a metric strip so the owner can
 * scan vertically without clicking in.
 *
 * Layout: identity claims flex-1 on the left, metric strip sits at a fixed
 * width on the right. No expanding middle column — empty space used to
 * land there and made the row feel sparse.
 */
export const ConversationRowsView = memo(
  ({ conversations, onViewDetails }: ConversationRowsViewProps) => {
    return (
      <div className="space-y-2.5">
        {conversations.map((conversation) => (
          <ConversationRowCard
            key={conversation.id}
            conversation={conversation}
            onViewDetails={onViewDetails}
          />
        ))}
      </div>
    );
  },
);

ConversationRowsView.displayName = "ConversationRowsView";

interface ConversationRowCardProps {
  conversation: Conversation;
  onViewDetails: (conversation: Conversation) => void;
}

const ConversationRowCard = memo(
  ({ conversation, onViewDetails }: ConversationRowCardProps) => {
    const signals = extractConversationSignals(conversation);
    const isViewed = conversation.viewed ?? false;
    const agentName = conversation.agent?.name || "Unknown Agent";
    const endedEarly = signals.statusBadge?.tone === "ended";
    const hasTalkBalance =
      signals.agentTalkPct !== null && signals.custTalkPct !== null;

    const handleClick = useCallback(() => {
      onViewDetails(conversation);
    }, [conversation, onViewDetails]);

    return (
      <div
        onClick={handleClick}
        className={cn(
          "group bg-white rounded-xl border transition-all duration-200 cursor-pointer hover:shadow-md hover:border-gray-300",
        )}
        style={
          signals.isUrgent
            ? {
                borderColor: "#FF4DB8",
                borderWidth: 2,
                borderLeftWidth: 4,
                backgroundColor: "rgba(255,77,184,0.04)",
                boxShadow: "0 0 14px rgba(255,77,184,0.12)",
              }
            : {
                borderColor: !isViewed ? signals.brandColor : "#E5EAF0",
                borderWidth: !isViewed ? 2 : 1,
                boxShadow: !isViewed ? `0 0 14px ${signals.brandColor}14` : undefined,
              }
        }
      >
        <div className="px-4 sm:px-5 py-3.5 flex items-center gap-4 sm:gap-6">
          {/* Identity (flex-1) */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0">
              <div
                className="rounded-full p-1.5"
                style={{
                  background: `radial-gradient(circle, ${signals.brandColor}15 0%, ${signals.brandColor}05 70%, transparent 100%)`,
                }}
              >
                {signals.logoUrl ? (
                  <img
                    src={signals.logoUrl}
                    alt={agentName}
                    className="w-10 h-10 object-contain"
                    style={{ filter: `drop-shadow(0 0 5px ${signals.brandColor}40)` }}
                  />
                ) : (
                  <div className="w-10 h-10">
                    <GenieAvatar
                      size={40}
                      color={signals.brandColor}
                      secondaryColor={signals.secondaryColor}
                      gradientEnabled={signals.hasGradient}
                      gradientAngle={signals.gradientAngle}
                      customAvatarUrl={signals.logoUrl}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <h3 className="min-w-0 truncate font-semibold text-gray-900 text-[15px] leading-tight">
                  <CopyableEmail email={signals.customerLabel ?? agentName} textClassName="truncate" />
                </h3>
                {signals.customerLabel && (
                  <span className="min-w-0 max-w-[45%] truncate text-xs text-gray-500">
                    {agentName}
                  </span>
                )}
                {!isViewed && (
                  <span
                    className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded text-white"
                    style={{ background: signals.brandColor }}
                  >
                    New
                  </span>
                )}
                {endedEarly && (
                  <span
                    className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded"
                    style={{ color: "#D6409F", background: "rgba(214,64,159,0.08)" }}
                  >
                    <XCircle className="w-2.5 h-2.5" />
                    Ended
                  </span>
                )}
                {!conversation.isSynced && (
                  <span
                    className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded"
                    style={{
                      color: "#FFBB33",
                      background: "rgba(255,187,51,0.12)",
                    }}
                  >
                    <Hourglass className="w-2.5 h-2.5" />
                    Not analyzed
                  </span>
                )}
                <span className="empty:hidden">
                  <ConversationAnnotationPills
                    conversationId={conversation.id}
                    dense
                    hideEmpty
                  />
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-1 flex-wrap">
                {signals.hasValidTime && (
                  <span>
                    {signals.timeAgo}
                    <span className="text-gray-400"> · </span>
                    {signals.exactTime}
                  </span>
                )}
                {signals.durationSeconds !== null && (
                  <>
                    <span className="text-gray-300">·</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDuration(signals.durationSeconds, true)}
                    </span>
                  </>
                )}
                {signals.msgCount !== null && signals.msgCount > 0 && (
                  <>
                    <span className="text-gray-300">·</span>
                    <span className="inline-flex items-center gap-1">
                      <MessageCircle className="w-3 h-3" />
                      {signals.msgCount}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Signal strip - fixed width, sits flush against identity */}
          <div className="hidden md:flex items-center gap-5 lg:gap-6 flex-shrink-0">
            <Metric label="Channel">
              <ConversationChannelPill channel={signals.channel} />
            </Metric>
            <Metric label="Attention">
              <ConversationAttentionPill attention={signals.attentionBadge} />
            </Metric>
            {hasTalkBalance && (
              <Metric label="Talk a/c">
                <span className="text-sm font-semibold text-gray-700">
                  {Math.round(signals.agentTalkPct!)}
                  <span className="text-gray-400">/</span>
                  {Math.round(signals.custTalkPct!)}
                </span>
              </Metric>
            )}
          </div>

          {/* Quick transcript grab — always visible, even on mobile */}
          <div className="flex-shrink-0">
            <TranscriptCopyButton conversation={conversation} compact />
          </div>
        </div>
      </div>
    );
  },
);

ConversationRowCard.displayName = "ConversationRowCard";

const Metric = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col items-end leading-none">
    <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-400 mb-1">
      {label}
    </span>
    {children}
  </div>
);
